import { useState, useRef, useEffect } from "react";
import { AutoSizer, Column, Table, defaultTableRowRenderer, defaultTableHeaderRenderer, SortDirection } from "react-virtualized";
import formatters from "../../config/formatters";
import { IoIosArrowForward, IoIosArrowDown } from "react-icons/io";
import useDataSuite from "../../AppMain/DataSuite/useDataSuite";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "reactstrap";
import { sortableHeaderRenderer } from "./SortableHeaderRenderer";
import { setItemLevelDescriptionData, setItemLevelDescriptionLoading } from "../../reducers/DatasuiteReducer/DataSuiteActions";

const CollapsibleDataTable = ({ list, tableColumns, tableName, widgetTitle, selectedFilters }) => {
  const dispatch = useDispatch();
  const { usCurrencyFormatter, numberFormatter, percentFormatter } = formatters();
  const { getNewLocationsItemLevel, getSalesByCategoryItemLevel, 
          getGrowthOpportunityItemLevel, getSalesByOperatorItemLevel, getSalesVoidItemLevel, getTargetsByCategoryItemLevel, getLeakageItemLevel } = useDataSuite.useDataSuite();
  const itemLevelDescriptionData = useSelector(({datasuite}) => datasuite.itemLevelDescriptionData);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const tableRef = useRef();
  const autoSizerRef = useRef();
  const [sortBy, setSortBy] = useState(widgetTitle === 'Match Opportunities' ? 'opportunity_cases' : '');
  const [sortDirection, setSortDirection] = useState(widgetTitle === 'Match Opportunities' ? SortDirection.DESC : null);
  const [childSortBy, setChildSortBy] = useState(widgetTitle === 'Match Opportunities' ? 'opportunity_cases' : '');
  const [childSortDirection, setChildSortDirection] = useState(widgetTitle === 'Match Opportunities' ? SortDirection.DESC : null);

  const formatData = (formatter, data) =>{
    switch (formatter) {
        case ("usCurrency") : {
            return usCurrencyFormatter.format(data);
        }
        case ("percentage") : {
            return percentFormatter.format(data);
        }
        case ("number") : {
            return numberFormatter.format(data);
        }
        default : {
            return data;
        }
    }
  }

  const columnMap = tableColumns?.map((item, idx) => {
    return ( 
    <Column
        key={idx}
        className={idx === 0 ? "searchHighlight": ''}
        label={item?.label}
        dataKey={item?.dataKey}
        width={item?.width > 1 ? item?.width : autoSizerRef?.current?.state?.width * item?.width}
        cellRenderer={item?.cellRenderer ? item?.cellRenderer : ({cellData}) => formatData(item?.formatter, cellData)}
        disableSort={item?.disableSort === 'true'}
        headerRenderer={item?.headerRenderer === 'true' ? sortableHeaderRenderer : defaultTableHeaderRenderer}
    />
    )
  });

  const sortTableData = ((tableList, activeSortBy = sortBy, activeSortDirection = sortDirection) => {
    if (!tableList) return [];
    const sorted = [...tableList];
    if (activeSortBy) {
      sorted.sort((a, b) => {
        const aValue = a[activeSortBy];
        const bValue = b[activeSortBy];
        // Handle undefined/null values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return activeSortDirection === SortDirection.ASC ? -1 : 1;
        if (bValue == null) return activeSortDirection === SortDirection.ASC ? 1 : -1;

        // Numeric sort if both are numbers, else string compare
        if (typeof aValue === "number" && typeof bValue === "number") {
            return activeSortDirection === SortDirection.ASC ? aValue - bValue : bValue - aValue;
        } else {
            // fallback to string comparison
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();
            if (aStr < bStr) return activeSortDirection === SortDirection.ASC ? -1 : 1;
            if (aStr > bStr) return activeSortDirection === SortDirection.ASC ? 1 : -1;
            return 0;
        }
      });
    }
    return sorted;
  });

  const itemLevelTable = (
        <Table className={"table border-grayLight pb-1"} id={"itemLevelTable"}
          headerHeight={34.5}
          height={226}
          overscanRowCount={10}
          rowHeight={38}
          rowGetter={({index}) => itemLevelDescriptionData[index]}
          rowCount={itemLevelDescriptionData?.length}
          width={autoSizerRef?.current?.state?.width * .9715}
        >
          <Column
          dataKey="description"
          label="Description"
          width={autoSizerRef?.current?.state?.width * .45}
          />
          <Column
          dataKey="category"
          label="Category"
          width={autoSizerRef?.current?.state?.width * .25}
          />
          <Column
          dataKey="pack"
          label="Packsize"
          width={autoSizerRef?.current?.state?.width * .15}
          />
          <Column
          dataKey="total_cases"
          label="Your Cases"
          width={autoSizerRef?.current?.state?.width * .15}
          cellRenderer={({cellData}) => formatData("number", cellData)}
          />
        </Table>
  );

  const childLocationTable = (() => {
    return (
      <Table className={"table border-grayLight pb-1"} id={"childLocationTable"}
          headerHeight={34.5}
          height={226}
          overscanRowCount={10}
          rowHeight={38}
          rowGetter={({index}) => sortTableData(itemLevelDescriptionData, childSortBy, childSortDirection)[index]}
          rowCount={sortTableData(itemLevelDescriptionData, childSortBy, childSortDirection)?.length}
          width={autoSizerRef?.current?.state?.width * .9715}
          sort={({ sortBy: sb, sortDirection: sd }) => {
            setChildSortBy(sb);
            setChildSortDirection(sd);
          }}
          sortBy={childSortBy}
          sortDirection={childSortDirection}
      >
          <Column
          dataKey="child_location_name"
          label="Location Name"
          width={autoSizerRef?.current?.state?.width * .25}
          headerRenderer={sortableHeaderRenderer}
          />
          <Column
          dataKey="child_location_city"
          label="Location"
          width={autoSizerRef?.current?.state?.width * .20}
          cellRenderer={({cellData, rowData}) => <div>{cellData + ', ' + rowData.child_location_state}</div>}
          headerRenderer={sortableHeaderRenderer}
          />
          <Column
          dataKey="your_cases"
          label="Your Cases"
          width={autoSizerRef?.current?.state?.width * .10}
          headerRenderer={sortableHeaderRenderer}
          />
          <Column
          dataKey="opportunity_cases"
          label="Opportunity Cases"
          width={autoSizerRef?.current?.state?.width * .15}
          cellRenderer={({cellData}) => formatData("number", cellData)}
          headerRenderer={sortableHeaderRenderer}
          />
          <Column
          dataKey="total_cases"
          label="Total Cases"
          width={autoSizerRef?.current?.state?.width * .15}
          cellRenderer={({cellData}) => formatData("number", cellData)}
          headerRenderer={sortableHeaderRenderer}
          />
      </Table>
    );
  })();

  const clickItemListDetails = (index) => {
    if (index >= 0) {
        switch (widgetTitle) {
            case ("New Locations") : {
              getNewLocationsItemLevel([sortTableData(list)[index].location_name], selectedFilters);
              break;
            }
            case ("Sales By Operator") : {
              getSalesByOperatorItemLevel([sortTableData(list)[index].location_name], [sortTableData(list)[index].category], selectedFilters);
              break;
            }
            case ("Sales By Category") : {
              getSalesByCategoryItemLevel([sortTableData(list)[index].category], selectedFilters);
              break;
            }
            case ("Growth Opportunities") : {
              getGrowthOpportunityItemLevel([sortTableData(list)[index].location_name], [sortTableData(list)[index].category], selectedFilters);
              break;
            }
            case ("Sales Void Opportunities") : {
              getSalesVoidItemLevel([sortTableData(list)[index].location_name], selectedFilters);
              break;
            }
            case ("Targets By Category") : {
              getTargetsByCategoryItemLevel([sortTableData(list)[index].category], selectedFilters);
              break;
            }
            case ("Leakage") : {
              getLeakageItemLevel([sortTableData(list)[index].location_name], [sortTableData(list)[index].category], selectedFilters);
              break;
            }
            case ("Match Opportunities") : {
              dispatch(setItemLevelDescriptionData(sortTableData(list)[index].child_locations));
              dispatch(setItemLevelDescriptionLoading(false));
              break;
            }
            default : {
                //Do nothing
            }
        }
    }
    setSelectedIndex(index); 
  };

  const Details = ({ children, index }) => (
    <div key={index} style={{ cursor: "pointer" }} onClick={() => clickItemListDetails(index)}>
      {children}
    </div>
  );

  const _getRowHeight = ({ index }) => (index === selectedIndex ? 300 : 38);
  const cellRenderer = ({ rowIndex }) => {
    if (rowIndex !== selectedIndex) {
      return <Details index={rowIndex}><IoIosArrowForward/></Details>;
    } else {
      return <Details index={-1}><IoIosArrowDown/></Details>;
    }
  };

  useEffect(() => {
    if (list.length === 1) {
      setSelectedIndex(0);
      clickItemListDetails(0);
    } else {
      setSelectedIndex(-1);
    }
    // eslint-disable-next-line
  }, [list]);

  useEffect(
    () => {
      tableRef.current?.recomputeRowHeights();
    },
    [selectedIndex]
  );

  useEffect(() => {
    tableRef.current?.recomputeRowHeights();
    tableRef.current?.forceUpdateGrid?.();
  }, [childSortBy, childSortDirection, itemLevelDescriptionData]);

  const rowRenderer = props => {
    const { index, style, className } = props;
    if (index === selectedIndex) {
      return (
        <div
          style={{ ...style, display: "flex", flexDirection: "column", paddingBottom: '0px' }}
          className={className}
          key={index}
        >
          {defaultTableRowRenderer({
            ...props,
            style: { width: style.width, height: 38, paddingBottom: '0.5rem' }
          })}
          <div className="py-3" style={{width: '100%', background: '#F0F0F2'}}>
            <div className="mx-auto" style={{overflowY: "none", width: "98%", background: "#FFFFFF", 'filter' : 'drop-shadow(0px 1px 1px gray)'}}>
              {itemLevelDescriptionData?.length > 0 && (widgetTitle === "Match Opportunities" ? childLocationTable : itemLevelTable)}
              {itemLevelDescriptionData?.length <=0 && 
                  <div className="mx-auto my-auto" style={{height:"228px", width:"2.5%"}}>
                      <Spinner style={{marginTop: "100px"}} color='beplBlue' animation="border" />
                  </div>
              }
            </div>
          </div>
        </div>
      );
    } else {
      return defaultTableRowRenderer(props);
    }
  };

  return (
    <div style={{ height: "100%" }}>
      <AutoSizer ref={autoSizerRef}>
        {({ width, height }) => (
          <Table className={"table border-grayLight " + tableName} id={tableName}
            headerHeight={34.5}
            height={height}
            overscanRowCount={10}
            rowHeight={_getRowHeight}
            rowGetter={({ index }) => sortTableData(list)[index]}
            rowCount={sortTableData(list).length}
            width={width}
            ref={tableRef}
            rowRenderer={rowRenderer}
            sort={({ sortBy: sb, sortDirection: sd }) => {
                setSortBy(sb);
                setSortDirection(sd);
                setSelectedIndex(-1);
            }}
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            <Column
              cellDataGetter={({ rowData }) => rowData?.length}
              cellRenderer={cellRenderer}
              dataKey="index"
              width={20}
            />
            {columnMap}
          </Table>
        )}
      </AutoSizer>
    </div>
  );
};

export default CollapsibleDataTable;
